import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MergeUsersService } from '@/services/users/MergeUsersService';
import { MergeJobStatusResponse, MergePreviewRequest, MergeRequest } from '@/types/users/merge';

export const useMergeUsersPreviewMutation = () => {
  return useMutation({
    mutationFn: (request: MergePreviewRequest) => MergeUsersService.preview(request),
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to preview merge');
    },
  });
};

export const useMergeUsersMutation = () => {
  return useMutation({
    mutationFn: (request: MergeRequest) => MergeUsersService.merge(request),
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start merge');
    },
  });
};

export const useMergeUsersRetryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: number) => MergeUsersService.retry(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: ['merge-users-job', jobId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to retry merge');
    },
  });
};

export const useMergeUsersJob = (jobId: number | null, enabled: boolean = true) => {
  const queryClient = useQueryClient();

  return useQuery<MergeJobStatusResponse | null>({
    queryKey: ['merge-users-job', jobId],
    queryFn: async () => {
      if (!jobId) return null;

      const job = await MergeUsersService.getJobStatus(jobId);
      const items = await MergeUsersService.getJobItems(jobId);
      const minorProfiles = await MergeUsersService.getProfilesByIds(job.minor_user_ids);

      const pending = items.filter((i) => i.status === 'pending').length;
      const failed = items.filter((i) => i.status === 'failed').length;
      const completed = items.filter((i) => i.status === 'completed').length;

      // The final job.status flip happens via a fire-and-forget self-invoke
      // after the last table finishes — if that invoke is dropped (cold
      // start, transient network error), every item can be done while the
      // job row is stuck on "running" forever. Treat "every expected table
      // has settled" as done regardless of what job.status says, so the UI
      // never hangs waiting on a status flip that already missed its chance.
      // total_tables is a snapshot taken at job creation, so this can't
      // false-positive mid-run on the incremental item upserts.
      const allSettled =
        job.total_tables != null && items.length >= job.total_tables && pending === 0;
      const effectiveStatus =
        job.status === 'completed' || job.status === 'failed'
          ? job.status
          : allSettled
            ? failed > 0
              ? 'failed'
              : 'completed'
            : job.status;

      if (effectiveStatus === 'completed' || effectiveStatus === 'failed') {
        queryClient.invalidateQueries({ queryKey: ['users-with-subscriptions'] });
      }

      return {
        job: { ...job, status: effectiveStatus },
        items,
        minorProfiles,
        progress: {
          total: items.length,
          completed,
          failed,
          pending,
        },
      };
    },
    enabled: enabled && jobId !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.job) return 2000;
      if (data.job.status === 'completed' || data.job.status === 'failed') return false;
      return 2000;
    },
  });
};
